/**
 * /zh/prioritization-frameworks — 优先级框架中文 SEO 页。与
 * PrioritizationFrameworks.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function PrioritizationFrameworksZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/prioritization-frameworks"
      documentTitle="优先级框架：RICE、MoSCoW、WSJF 全解 · Draftlize"
      documentDescription="PM 真正在用的优先级框架——RICE、MoSCoW、WSJF、Kano、加权打分——每个都配公式与算例，讲清何时用，并附一份可抄的决策矩阵。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 优先级</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            优先级框架，<br/>
            <em>以及每一个各自值在哪。</em>
          </h1>
          <p className="lede">
            每个优先级框架的存在，都是为了把一场争论变成一个你拿得出手去辩护的数字。RICE、MoSCoW、WSJF、Kano、加权打分——它们是同一个问题的不同镜片：<em>我们接下来该做什么，又有多大把握？</em>这页把每一个都配上真正的公式和一个走完一遍的算例，再给你一张「什么时候用哪个」的直白表格。然后是模板都跳过的那部分——一个分数只在它的某个输入没变之前为真，而在电子表格里，没有任何东西会在输入变了时告诉你。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么要排优先级<em>（以及分数为什么会腐烂）。</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            排优先级，无非是把取舍从「隐含」变成「明说」。你的好点子总比产能多；框架逼你用一套能对着怀疑的干系人复述的说法，讲清<em>为什么</em>这个胜过那个。数字本身不是重点——数字背后那套达成共识的<em>输入</em>才是。一个 42 分的 RICE 分数什么都不说明；「触达 8k、影响 2、把握 80%、成本 3 人月」才说明问题，而且是以正确的方式可被争论。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            而这恰恰是优先级会衰减的原因。那些输入是对一个移动世界的假设。触达是在人群变化前估的；成本是在那次技术尖刺前猜的；把握在实验跑出平平结果之前还很高。你一月份辩护过的排序，到三月已经悄悄错了——而表格看起来和你填它那天一样自信。框架给你一个好决策；电子表格里没有任何东西让这个决策保持「好」。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">五种框架</span>
            <h2 className="serif">五个镜片，<em>带真正的算法。</em></h2>
          </div>
          <p>每个都配公式、一行算例、以及它为之而生的场景。没有哪个「最好」——它们以不同方式在严谨和速度之间取舍。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'RICE', hint: '触达·影响·把握÷成本', body: '分数 =（触达 × 影响 × 把握）÷ 成本。例：（8,000 用户 × 2 × 0.8）÷ 3 = 4,267。适合你手上有粗略的量化输入、又想在差异很大的功能间得到一个可比分数时。「把握」那一项是诚实的部分——它会扣掉你拿不出依据的点子。' },
            { k: 'MoSCoW', hint: 'Must·Should·Could·Won\'t', body: '把每一项分到 Must（必须）、Should（应该）、Could（可以）、Won\'t（这次不做）。没有数学——它是快速对齐工具，最适合和干系人在一个房间里圈定一个发布的范围。它的纪律在「Won\'t」那一桶：点名你「刻意不做」的东西，能挡住悄悄的范围蔓延。' },
            { k: 'WSJF', hint: '延迟成本 ÷ 工作量', body: '加权最短作业优先（来自 SAFe/敏捷）= 延迟成本 ÷ 工作量。能浮出那些被纯影响分数埋掉的、小而时效性强的事。适合给一个「时机要紧」的 backlog 排序——一件现在值很多、以后值很少的事会得高分。' },
            { k: 'Kano 模型', hint: '基本·性能·惊喜', body: '按对满意度的影响给功能分类：基本型（缺了难受，有了无感）、性能型（越多越好）、惊喜型（意料之外的加分）。适合平衡路线图，避免在某个「基本期望」还缺失时，过度投资于惊喜。' },
            { k: '加权打分', hint: '自定义标准 × 权重', body: '一张决策矩阵：选标准（影响、成本、战略契合、风险），给权重，给每个选项打分，求和。最灵活——你来定义什么重要。适合在少数几个选项之间做一次性、高风险的抉择，此时 RICE 固定的输入不合身。（这就是「加权决策矩阵」。）' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">怎么选</span>
            <h2 className="serif">什么时候用<em>哪个。</em></h2>
          </div>
          <p>让工具匹配决策，而不是反过来。一份速查。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>你的处境</th>
              <th>用</th>
              <th>为什么</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>用粗略数据比较很多功能</td><td className="yes">RICE</td><td>一个可比、按把握折算的分数</td></tr>
            <tr><td>和干系人圈定一个发布范围</td><td className="yes">MoSCoW</td><td>快速对齐，逼出一份「不做」清单</td></tr>
            <tr><td>时机要紧的排序</td><td className="yes">WSJF</td><td>浮出又小又急又便宜的赢面</td></tr>
            <tr><td>平衡基本盘与惊喜</td><td className="partial">Kano</td><td>按对满意度的影响分类</td></tr>
            <tr><td>少数选项里的一次高风险抉择</td><td className="yes">加权矩阵</td><td>标准和权重你自己定</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">分数就是一个决策。<em>就当它是。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            优先级分数不是事实——它是由输入搭起来的决策。框架有多「活」，全看那些输入，而电子表格根本不知道其中哪个动了。这正是 Draftlize 做的那件窄事。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">输入是卡片，不是单元格</h3>
            <p>触达、影响、把握、成本——每一个都是住在某处的假设。在 Draftlize 里它们是卡片：触达估算引用它背后的分析，把握等级引用设定它的那次实验。分数连到它的输入，而不是把数值硬编码进一个没人回看的单元格。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">输入一变，排序标 stale</h3>
            <p>实验跑出平平结果，于是把握下降。持有旧把握的那张卡片变了，所有依赖它的分数自动标记 stale——于是「重排优先级」是被世界的移动触发的，而不是被某人碰巧重开表格触发的。排序之所以保持诚实，是因为由 substrate、而不是你的记忆，来追踪它压在什么上面。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 对着真相重新打分</h3>
            <p>让 Claude Code 或 Cursor 给 backlog 重排，它经 MCP 读到的是当前的输入——最新的触达、更新过的把握——而不是你上个季度敲进去的数值。框架于是变成一个会对着现实重跑的东西，而不是一张你在它早已不成立之后还在辩护的快照。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          优先级框架给你一个好决策。当它底下的数字移动时，电子表格里没有任何东西让这个决策保持「好」。
        </blockquote>
        <cite>留着你的框架，把它依赖的输入追踪起来，让排序无法悄悄腐烂。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '最容易上手的优先级框架是哪个？', a: 'MoSCoW。它不需要数学也不需要数据——你把条目分到 Must、Should、Could、Won\'t 四桶。这是让一屋子人对一个发布范围达成一致最快的方式；等你有了粗略的量化输入，再升级到 RICE。' },
            { q: 'RICE 和 WSJF 有什么区别？', a: 'RICE 优化的是「单位成本的整体价值」，并按把握折算，适合宽泛地比较很多功能；WSJF（延迟成本 ÷ 工作量）优化的是「时机要紧时的排序」，能浮出 RICE 会埋掉的又小又急又便宜的赢面。用 RICE 来选，用 WSJF 来排。' },
            { q: '怎么客观地给影响或把握打分？', a: '你做不到——它们就是估计，假装能客观才是陷阱。价值在于把每个数字背后的假设写下来，让它可被质疑、可被回看。80% 的把握应当注明「为什么」；当这个「为什么」变了，分数也该跟着变。' },
            { q: '该多久重排一次优先级？', a: '在某个输入变化时，而不是按固定日历。每月重排一次，是「不知道哪个假设动了」的一种将就。如果你的输入被追踪着，重排优先级就由那个真正让旧排序失效的东西来触发。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让你的排序保持诚实<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用你喜欢的任何框架打分。把输入作为卡片放进 Draftlize，让一个变化的假设去标记所有依赖它的排序——这样优先级是在世界移动时重排，而不是在你想起来时。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
